/**
 * Claude's answer as simple Markdown: headings, paragraphs, lists, code blocks,
 * bold/italic/code and [[page links]] that open the page. Rendered as React
 * elements (never as HTML), so nothing in an answer can inject markup.
 */
import { Fragment, type ReactNode } from 'react';

const INLINE =
  /(\[\[[^\]\n]+\]\]|\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\s][^*\n]*\*|\[[^\]\n]+\]\(https?:\/\/[^)\s]+\))/g;

function inline(text: string, onLink: (title: string) => void): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    if (part.startsWith('[[') && part.endsWith(']]')) {
      const inner = part.slice(2, -2);
      const [target = '', label] = inner.split('|');
      const title = target.split('#')[0]!.trim();
      return (
        <button
          key={i}
          type="button"
          onClick={() => onLink(title)}
          className="text-accent-ink underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
        >
          {label ?? target}
        </button>
      );
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4)
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2)
      return (
        <code key={i} className="rounded bg-bg-hover px-1 text-[0.85em]">
          {part.slice(1, -1)}
        </code>
      );
    if (/^\*[^*].*\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
    const link = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/.exec(part);
    if (link)
      return (
        <a key={i} href={link[2]} target="_blank" rel="noopener noreferrer" className="underline">
          {link[1]}
        </a>
      );
    return <Fragment key={i}>{part}</Fragment>;
  });
}

type Block =
  | { kind: 'p' | 'h'; text: string; level?: number }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'code'; text: string };

function blocks(md: string): Block[] {
  const out: Block[] = [];
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.startsWith('```')) {
      const code: string[] = [];
      while (++i < lines.length && !lines[i]!.startsWith('```')) code.push(lines[i]!);
      out.push({ kind: 'code', text: code.join('\n') });
      continue;
    }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      out.push({ kind: 'h', level: heading[1]!.length, text: heading[2]! });
      continue;
    }
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      const kind = bullet ? 'ul' : 'ol';
      const last = out[out.length - 1];
      const item = (bullet ?? numbered)![1]!;
      if (last && last.kind === kind) last.items.push(item);
      else out.push({ kind, items: [item] });
      continue;
    }
    if (!line.trim()) {
      out.push({ kind: 'p', text: '' });
      continue;
    }
    const last = out[out.length - 1];
    if (last?.kind === 'p' && last.text) last.text += `\n${line}`;
    else out.push({ kind: 'p', text: line });
  }
  return out.filter((b) => b.kind !== 'p' || b.text);
}

export function AnswerText({ text, onLink }: { text: string; onLink: (title: string) => void }) {
  return (
    <div className="flex flex-col gap-2 leading-relaxed">
      {blocks(text).map((b, i) => {
        switch (b.kind) {
          case 'h':
            return (
              <p key={i} className={b.level! <= 2 ? 'font-semibold' : 'font-medium'}>
                {inline(b.text, onLink)}
              </p>
            );
          case 'ul':
          case 'ol': {
            const List = b.kind;
            return (
              <List key={i} className={b.kind === 'ul' ? 'list-disc pl-5' : 'list-decimal pl-5'}>
                {b.items.map((item, j) => (
                  <li key={j}>{inline(item, onLink)}</li>
                ))}
              </List>
            );
          }
          case 'code':
            return (
              <pre key={i} className="overflow-x-auto rounded bg-bg-hover p-2 text-xs">
                {b.text}
              </pre>
            );
          default:
            return (
              <p key={i} className="whitespace-pre-wrap">
                {inline(b.text, onLink)}
              </p>
            );
        }
      })}
    </div>
  );
}
